import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';

type AuthState = {
  user: User | null;
  session: any;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  login: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  initialize: () => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  setSession: (session: any) => void;
};

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  session: null,
  loading: false,
  error: null,
  initialized: false,

  initialize: async () => {
    try {
      // Check if there's a token in the URL
      const url = new URL(window.location.href);
      const token = url.searchParams.get('token');
      
      if (token) {
        // If there's a token, try to log in with it
        try {
          await useAuthStore.getState().loginWithToken(token);
          // Remove the token from the URL to prevent reuse
          url.searchParams.delete('token');
          window.history.replaceState({}, document.title, url.toString());
          return;
        } catch (tokenError) {
          console.error('Token login error:', tokenError);
          // Continue with normal initialization if token login fails
        }
      }

      // Get the current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        await supabase.auth.signOut();
        set({ 
          user: null,
          session: null,
          initialized: true,
          loading: false,
          error: null
        });
        return;
      }

      // Set up auth state change listener
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
        if (event === 'SIGNED_OUT' || event === 'USER_DELETED' || !currentSession) {
          set({ 
            user: null,
            session: null,
            initialized: true,
            loading: false,
            error: null
          });
          return;
        }

        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          set({ 
            user: currentSession.user,
            session: currentSession,
            initialized: true,
            loading: false,
            error: null
          });
          
          // Log login event
          try {
            await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-auth-events`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                event: 'login',
                userId: currentSession.user.id,
                details: { method: 'session' }
              }),
            });
          } catch (error) {
            console.error('Failed to log login event:', error);
          }
        }
      });

      // Set initial state based on session existence
      if (session?.user) {
        set({ 
          user: session.user,
          session: session,
          initialized: true,
          loading: false,
          error: null
        });
        
        // Log login event for initial session
        try {
          await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-auth-events`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              event: 'session_restored',
              userId: session.user.id,
              details: { method: 'initialize' }
            }),
          });
        } catch (error) {
          console.error('Failed to log session restore event:', error);
        }
      } else {
        set({ 
          user: null,
          session: null,
          initialized: true,
          loading: false,
          error: null
        });
      }

      // Return cleanup function
      return () => {
        subscription.unsubscribe();
      };
    } catch (error: any) {
      console.error('Auth initialization error:', error);
      await supabase.auth.signOut();
      set({ 
        user: null,
        session: null,
        error: error.message || 'Failed to initialize authentication',
        loading: false,
        initialized: true
      });
    }
  },

  login: async (email: string, password: string) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      if (!data.user || !data.session) {
        throw new Error('Login failed - invalid response');
      }

      set({ 
        user: data.user,
        session: data.session,
        loading: false,
        error: null
      });
      
      // Log login event
      try {
        await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-auth-events`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            event: 'login',
            userId: data.user.id,
            details: { method: 'password' }
          }),
        });
      } catch (error) {
        console.error('Failed to log login event:', error);
      }
    } catch (error: any) {
      console.error('Login error:', error);
      await supabase.auth.signOut();
      set({ 
        user: null,
        session: null,
        error: error.message,
        loading: false
      });
    }
  },

  loginWithToken: async (token: string) => {
    set({ loading: true, error: null });
    try {
      // Call the Supabase Edge Function to handle token login
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/token-login`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error: ${response.status}`);
      }

      const { success, redirectUrl, error } = await response.json();
      
      if (!success || error) {
        throw new Error(error || 'Token login failed');
      }

      // If we have a redirect URL, use it to complete the login flow
      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }

      // If no redirect URL but success is true, refresh the session
      const { data: { session }, error: refreshError } = await supabase.auth.getSession();
      
      if (refreshError) {
        throw refreshError;
      }
      
      if (session) {
        set({ 
          user: session.user,
          session,
          loading: false,
          error: null
        });
        
        // Log token login event
        try {
          await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-auth-events`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              event: 'login',
              userId: session.user.id,
              details: { method: 'token' }
            }),
          });
        } catch (error) {
          console.error('Failed to log token login event:', error);
        }
      } else {
        throw new Error('No session after token login');
      }
    } catch (error: any) {
      console.error('Token login error:', error);
      set({ 
        user: null,
        session: null,
        error: error.message || 'Token login failed',
        loading: false
      });
      throw error;
    }
  },

  signUp: async (email: string, password: string) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });
      
      if (error) throw error;
      
      if (!data.user) {
        throw new Error('Sign up failed - no user data');
      }

      set({ 
        user: data.user,
        session: data.session,
        loading: false,
        error: null
      });
      
      // Log signup event
      if (data.user) {
        try {
          await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-auth-events`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              event: 'signup',
              userId: data.user.id,
              details: { email: data.user.email }
            }),
          });
        } catch (error) {
          console.error('Failed to log signup event:', error);
        }
      }
    } catch (error: any) {
      console.error('Sign up error:', error);
      await supabase.auth.signOut();
      set({ 
        user: null,
        session: null,
        error: error.message,
        loading: false
      });
    }
  },

  logout: async () => {
    set({ loading: true });
    try {
      // Log logout event before signing out
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        try {
          await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-auth-events`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              event: 'logout',
              userId: currentUser.id,
              details: { email: currentUser.email }
            }),
          });
        } catch (error) {
          console.error('Failed to log logout event:', error);
        }
      }
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        // Check if the error is due to session already being missing
        if (error.message && (
          error.message.includes('Session from session_id claim in JWT does not exist') ||
          error.message.includes('session_not_found') ||
          error.message.includes('Auth session missing')
        )) {
          // Session is already missing on server, just clear local state
          console.log('Session already cleared on server, clearing local state');
        } else {
          // For other types of errors, throw them
          throw error;
        }
      }

      // Always clear local state regardless of server response
      set({ 
        user: null,
        session: null,
        loading: false,
        error: null
      });
    } catch (error: any) {
      console.error('Logout error:', error);
      // Even if logout fails on server, clear local state
      set({ 
        user: null,
        session: null,
        loading: false,
        error: null
      });
    }
  },

  setSession: (session) => {
    set({ 
      user: session?.user || null,
      session,
      loading: false,
      error: null
    });
  }
}));